import React, { useMemo, useEffect } from "react";

import { Link, useLocation, useMatch } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan } from "@fortawesome/pro-solid-svg-icons";

import Loading from "ui/Loading";
import { useRelatedRoute, useRoutes } from "utils/routes";
import { useUserPreferences } from "utils/hooks";
import { mru } from "utils/array";

import {
  type DatabaseGroupSelectQuery,
  useDatabaseGroupSelectQuery,
} from "./gql/Query.generated";

type DatabaseGroupType = DatabaseGroupSelectQuery["getDatabaseGroups"][number];

import styles from "../style.module.scss";
import { useCurrentServerGroup } from "providers/WithCurrentOrganization";
import { CaretIcon, CloseIcon } from "ui/Icons";
import MenuList from "../MenuList";
import { ItemRendererProps } from "../types";

function DatabaseGroupSelect() {
  const currentServerGroup = useCurrentServerGroup();
  const currentServerGroupId = currentServerGroup?.id ?? "";
  const { search } = useLocation();
  const currentDatname = new URLSearchParams(search).get("datname");
  const isEditingServerGroup =
    useMatch("/servergroups/:serverGroupId/edit") != null;
  const isQueryZoom = useMatch("/queries/:fingerprint") != null;

  const [dbgSelect, setDbgSelect] = useUserPreferences("databaseGroupSelect");

  const recentDatnames = useMemo(() => {
    return (dbgSelect.recentDatnames || {})[currentServerGroupId] || [];
  }, [dbgSelect.recentDatnames, currentServerGroupId]);

  const { relatedServerGroupRoute } = useRelatedRoute();

  useEffect(() => {
    if (currentDatname && recentDatnames[0] != currentDatname) {
      setDbgSelect({
        ...dbgSelect,
        recentDatnames: {
          ...dbgSelect.recentDatnames,
          [currentServerGroupId]: mru(currentDatname, recentDatnames, 5),
        },
      });
    }
  }, [
    setDbgSelect,
    dbgSelect,
    currentServerGroupId,
    recentDatnames,
    currentDatname,
  ]);

  const { data, loading, error } = useDatabaseGroupSelectQuery({
    variables: {
      serverGroupId: currentServerGroupId,
    },
  });
  if (loading || error) {
    return <Loading className={styles.loading} small error={!!error} />;
  }

  const groups = data?.getDatabaseGroups ?? [];
  const visibleGroups = groups.filter((g) => !g.hidden);

  const sortedGroups = visibleGroups.slice().sort((a, b) => {
    if (
      recentDatnames.includes(a.datname) &&
      recentDatnames.includes(b.datname)
    ) {
      return (
        recentDatnames.indexOf(a.datname) - recentDatnames.indexOf(b.datname)
      );
    }
    if (recentDatnames.includes(a.datname)) {
      return -1;
    }
    if (recentDatnames.includes(b.datname)) {
      return 1;
    }
    return a.datname.localeCompare(b.datname);
  });

  const currentGroup = groups.find((g) => g.datname === currentDatname);
  // The dropdown is active when when there is a meaningful choice to make,
  // like having multiple groups to select or exactly 1 and it is not currently selected.
  // The dropdown is inactive when either case above is false, or the user is viewing server group settings.
  const canOpen =
    (visibleGroups.length > 1 ||
      (visibleGroups.length === 1 && !currentGroup)) &&
    !isEditingServerGroup;

  const trigger = ({
    open,
    toggleOpen,
  }: {
    open: boolean;
    toggleOpen: () => void;
  }) => (
    <button onClick={toggleOpen}>
      <small>Database</small>
      <div className={styles.selectMenuCurrent}>
        {currentGroup ? (
          <span>{currentGroup.datname}</span>
        ) : (
          <span>No database selected</span>
        )}
        {currentGroup && !isQueryZoom && (
          <Link
            to={relatedServerGroupRoute(currentServerGroupId)}
            className={styles.closeLink}
          >
            <CloseIcon />
          </Link>
        )}
        <CaretIcon direction={open ? "up" : "down"} headerNav />
      </div>
    </button>
  );

  return (
    <div className={styles.selectMenu} data-select-menu="databaseGroup">
      {canOpen ? (
        <MenuList
          trigger={trigger}
          prompt="Search databases..."
          noMatch="no databases match"
          sections={[{ entries: sortedGroups }]}
          filterKeys={["datname"]}
          renderer={DatabaseGroupItem}
        />
      ) : (
        <div>
          <div className={styles.noOptions}>
            <small>Database</small>
            <div className={styles.selectMenuCurrent}>
              {currentGroup ? (
                <span>{currentGroup.datname}</span>
              ) : (
                <FontAwesomeIcon
                  icon={faBan}
                  className="text-[#ccc] mt-[1px]"
                />
              )}
              {currentGroup && !isQueryZoom && (
                <Link
                  to={relatedServerGroupRoute(currentServerGroupId)}
                  className={styles.closeLink}
                >
                  <CloseIcon />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DatabaseGroupItem({
  item: group,
  onSelect,
}: ItemRendererProps<DatabaseGroupType & { datname: string }>) {
  const { serverGroupQueries, database: databaseRoot } = useRoutes();
  const { relatedDatabaseRoute, relatedDatabaseGroupRoute } = useRelatedRoute();
  const currentServerGroup = useCurrentServerGroup();
  const serverGroupId = currentServerGroup?.id ?? "";

  // Pages with a database group equivalent (e.g. the query zoom) stay on it.
  // For pages with a database-scoped equivalent (e.g. workbooks), drill into
  // the first matching database. Otherwise, keep the datname-filtered server
  // group queries behavior.
  const firstDatabaseId = group.databaseIds[0];
  const relatedRoute = firstDatabaseId
    ? relatedDatabaseRoute(firstDatabaseId)
    : null;
  const databaseRoute =
    firstDatabaseId && relatedRoute !== databaseRoot(firstDatabaseId)
      ? relatedRoute
      : null;
  const to =
    relatedDatabaseGroupRoute(serverGroupId, group.datname) ??
    databaseRoute ??
    serverGroupQueries(serverGroupId, group.datname);

  return (
    <Link className={styles.selectMenuListItem} to={to} onClick={onSelect}>
      <span>{group.datname}</span>
    </Link>
  );
}

export default DatabaseGroupSelect;
